import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v2.94 · Motor Starting graphical editor. Simple 2-node star:
   - 1 SYSTEM  (system_voltage_kv, system_sc_mva)
   - 1 MOTOR   (motor_hp, motor_pf_locked_rotor, motor_locked_rotor_kva_per_hp, starting_method)
   Additional motors are ignored — the /api/studies/motor-starting
   endpoint models a single motor start against a stiff system. */

const System = (p) => <BusNode {...p} {...NODE_STYLE.SYSTEM}
  sub={(d) => `${d.system_voltage_kv} kV · ${d.system_sc_mva} MVA`} />;
const Motor  = (p) => <BusNode {...p} {...NODE_STYLE.MOTOR}
  sub={(d) => `${d.motor_hp} HP · ${d.starting_method}`} />;
const nodeTypes = { SYSTEM: System, MOTOR: Motor };

const INITIAL_NODES = [
  { id: "SYS", type: "SYSTEM", position: { x: 80, y: 160 },
    data: { id: "SYS", system_voltage_kv: 0.48, system_sc_mva: 30 } },
  { id: "M1", type: "MOTOR", position: { x: 360, y: 160 },
    data: { id: "M1", motor_hp: 500, motor_pf_locked_rotor: 0.3,
             motor_locked_rotor_kva_per_hp: 6.0, starting_method: "DOL" } },
];
const INITIAL_EDGES = [
  { id: "e1", source: "SYS", target: "M1", ...edgeStyle() },
];

const FIELDS = {
  SYSTEM: [
    { key: "id",                 label: "Tag",                  type: "text" },
    { key: "system_voltage_kv",  label: "System V (kV L-L)",    type: "number", step: "0.01" },
    { key: "system_sc_mva",      label: "System SC MVA",        type: "number" },
  ],
  MOTOR: [
    { key: "id",                              label: "Tag",              type: "text" },
    { key: "motor_hp",                        label: "Motor HP",         type: "number" },
    { key: "motor_pf_locked_rotor",           label: "PF (locked-rotor)", type: "number", step: "0.01" },
    { key: "motor_locked_rotor_kva_per_hp",   label: "LRA kVA / HP",     type: "number", step: "0.1" },
    { key: "starting_method",                 label: "Start method",     type: "select",
      options: ["DOL", "STAR_DELTA", "SOFT", "VFD"] },
  ],
};

export function graphToMotorStart(nodes) {
  const sys = nodes.find((n) => n.type === "SYSTEM")?.data || {};
  const mot = nodes.find((n) => n.type === "MOTOR")?.data  || {};
  return {
    system_voltage_kv:              Number(sys.system_voltage_kv || 0.48),
    system_sc_mva:                  Number(sys.system_sc_mva || 30),
    motor_hp:                       Number(mot.motor_hp || 500),
    motor_pf_locked_rotor:          Number(mot.motor_pf_locked_rotor || 0.3),
    motor_locked_rotor_kva_per_hp:  Number(mot.motor_locked_rotor_kva_per_hp || 6.0),
    starting_method:                mot.starting_method || "DOL",
  };
}

function MotorStartSLDInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToMotorStart(nodes)); }, [nodes, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, id: `e${idCounter.current++}`, ...edgeStyle() }, es));
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type[0]}${idCounter.current++}`;
    const defaults = type === "SYSTEM"
      ? { system_voltage_kv: 0.48, system_sc_mva: 30 }
      : { motor_hp: 500, motor_pf_locked_rotor: 0.3, motor_locked_rotor_kva_per_hp: 6, starting_method: "DOL" };
    setNodes((ns) => [...ns, {
      id, type, position: { x: 120 + Math.random() * 400, y: 60 + Math.random() * 260 },
      data: { id, ...defaults },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };
  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="ms-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#FFCC00" onClick={() => addNode("SYSTEM")} testid="ms-add-system" title="Add system source">
          <Plus size={10} className="inline" /> system
        </ToolbarBtn>
        <ToolbarBtn colour="#00E5FF" onClick={() => addNode("MOTOR")} testid="ms-add-motor" title="Add motor">
          <Plus size={10} className="inline" /> motor
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel} testid="ms-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll} testid="ms-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.id}
          subtitle={sel.node.type.toLowerCase()}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function MotorStartSLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <MotorStartSLDInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
